/**
 * AdminAppUsers (26 Oct 2026) — admin tab "👥 App Users".
 * App users per country (from the country chosen at registration) and per species interest.
 * Counts only (no names / emails). Data: callable getAppUserStats (admins only, portal codebase).
 */
import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase-config';
import { getSpeciesName } from '../../shared/speciesUtils';

const spName = (id) => { const n = getSpeciesName(id); return n && n !== 'Unknown' ? n : `Species ${id}`; };

const flag = (code) => (/^[A-Z]{2}$/.test(code || '')
  ? String.fromCodePoint(...[...code].map((c) => 0x1F1E6 + c.charCodeAt(0) - 65)) : '🏳️');

const box = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 };
const bar = (pct) => ({ height: 8, borderRadius: 4, background: '#4fc3f7', width: `${Math.max(2, pct)}%` });
const row = { display: 'grid', gridTemplateColumns: 'minmax(140px, 1.2fr) 70px 2fr', gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 14 };

export default function AdminAppUsers() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async (fresh) => {
    setLoading(true);
    setError('');
    try {
      const r = await httpsCallable(functions, 'getAppUserStats')({ fresh: !!fresh });
      setData(r.data);
    } catch (e) {
      setError(e.message || 'Could not load the numbers.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(false); }, []);

  const maxC = data && data.byCountry.length ? data.byCountry[0].users : 1;
  const maxS = data && data.bySpecies.length ? data.bySpecies[0].users : 1;

  return (
    <div className="content-section">
      <div className="section-header">
        <h2>👥 App Users</h2>
        <button className="refresh-btn" onClick={() => load(true)} disabled={loading}>{loading ? 'Loading…' : '🔄 Refresh'}</button>
      </div>
      {error && <div style={{ ...box, color: '#fca5a5' }}>{error}</div>}

      {data && (
        <>
          <div style={{ ...box, display: 'flex', flexWrap: 'wrap', gap: '8px 24px', fontSize: 15 }}>
            <span><b>{data.appUsers}</b> app users</span>
            <span><b>{data.reachable}</b> can receive partner push notifications</span>
            <span><b>{data.byCountry.length}</b> countries</span>
            <span><b>{data.bySpecies.length}</b> species followed</span>
            <span style={{ opacity: 0.6, fontSize: 13 }}>updated {new Date(data.builtAt).toLocaleTimeString()}</span>
          </div>

          <div style={box}>
            <h3 style={{ marginTop: 0 }}>🌍 Where they are (country chosen at registration)</h3>
            {data.byCountry.map((c) => (
              <div key={c.code} style={row}>
                <span>{flag(c.code)} {c.name}</span>
                <b>{c.users}</b>
                <div style={bar((100 * c.users) / maxC)} />
              </div>
            ))}
            {data.noCountry > 0 && (
              <div style={{ ...row, opacity: 0.6 }}><span>No country given</span><b>{data.noCountry}</b><span /></div>
            )}
          </div>

          <div style={box}>
            <h3 style={{ marginTop: 0 }}>🐋 Species they are interested in</h3>
            {data.bySpecies.map((s) => (
              <div key={s.id} style={row}>
                <span>{spName(s.id)}</span>
                <b>{s.users}</b>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={bar((100 * s.users) / maxS)} />
                  <span style={{ fontSize: 12, opacity: 0.6, whiteSpace: 'nowrap' }}>{s.reachable} with push on</span>
                </div>
              </div>
            ))}
            {data.noSpecies > 0 && (
              <div style={{ ...row, opacity: 0.6 }}><span>No species chosen</span><b>{data.noSpecies}</b><span /></div>
            )}
          </div>
          <p style={{ fontSize: 13, opacity: 0.6 }}>
            App user = an account created in the Cetalingua FM app. Portal-only accounts (advertisers, stations, analysts) are not counted.
          </p>
        </>
      )}
    </div>
  );
}
